/**
 * What a Team reads (YAG-986): the one read behind the Connections panel in
 * Team settings and the Team page's reads line. Null until read; `error`
 * when the read did not land, so the panel can say so instead of showing an
 * empty list as the truth. One runner serves the first read and every
 * refetch: a read that lands after a newer one started, or after the
 * component went away, changes nothing (the unmount bumps the generation).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import type { TeamReadsResponse } from "@yagni/shared";

import { captureUiError } from "../sentry.js";
import { fetchTeamReads } from "./teamsApi.js";

export interface TeamReadsState {
  reads: TeamReadsResponse | null;
  error: boolean;
  refetch: () => Promise<void>;
}

export function useTeamReads(deskId: string | null): TeamReadsState {
  const [reads, setReads] = useState<TeamReadsResponse | null>(null);
  const [error, setError] = useState(false);
  // The newest read wins; an older one, or one from before an unmount, is dropped.
  const generation = useRef(0);

  const load = useCallback(async () => {
    if (!deskId) return;
    const mine = ++generation.current;
    const current = () => generation.current === mine;
    try {
      const next = await fetchTeamReads(deskId);
      if (!current()) return;
      setReads(next);
      setError(false);
    } catch (err) {
      if (!current()) return;
      captureUiError(err, {
        area: "Teams",
        operation: "team_reads_read",
        deskId,
        severity: "warning",
      });
      setError(true);
    }
  }, [deskId]);

  useEffect(() => {
    setReads(null);
    setError(false);
    void load();
    return () => {
      generation.current += 1;
    };
  }, [load]);

  return { reads, error, refetch: load };
}
